import type { Call } from '@/shared/api/types'

/**
 * Знак итога разговора.
 *
 * Рядом с иконкой всегда стоит слово: девять итогов девятью значками —
 * это ребус, который придётся заучивать. Иконка работает на скорость
 * — по ней строка опознаётся боковым зрением, — а слово на точность.
 */
export function OutcomeIcon({ outcome }: { outcome?: string }) {
  switch (outcome) {
    case 'appointment':
      return (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <rect x="1.6" y="2.6" width="10.8" height="10" rx="2" stroke="currentColor" strokeWidth="1.4" />
          <path d="M4.6 1.2V3.6M9.4 1.2V3.6M1.6 5.8H12.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M5 9L6.4 10.4L9.2 7.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )

    case 'callback':
      return (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="5.4" stroke="currentColor" strokeWidth="1.4" />
          <path d="M7 4.2V7.2L9 8.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )

    case 'interested':
      return (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M4.8 5.2C4.8 3.9 5.8 3 7 3C8.2 3 9.2 3.9 9.2 5.1C9.2 6.6 7 6.8 7 8.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="7" cy="10.8" r="0.9" fill="currentColor" />
        </svg>
      )

    case 'not_dm':
      return (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="5.4" cy="5" r="2.2" stroke="currentColor" strokeWidth="1.4" />
          <path d="M1.6 12C1.6 9.8 3.3 8.4 5.4 8.4C6.3 8.4 7.1 8.6 7.8 9.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M9 11L11 11M12.4 11L11 11M11 11L11 9.6M11 11L11 12.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      )

    case 'refusal':
    case 'no_interest':
      return (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="5.4" stroke="currentColor" strokeWidth="1.4" />
          <path d="M4.8 4.8L9.2 9.2M9.2 4.8L4.8 9.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      )

    case 'hangup':
      return (
        // Трубка, повёрнутая вниз: разговор оборвали.
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M2 8.6C2 6.2 4.2 4.6 7 4.6C9.8 4.6 12 6.2 12 8.6C12 9.2 11.6 9.6 11 9.6H9.8C9.3 9.6 8.9 9.2 8.9 8.7V8C8.9 7.6 8.6 7.3 8.2 7.2C7.8 7.1 7.4 7.1 7 7.1C6.6 7.1 6.2 7.1 5.8 7.2C5.4 7.3 5.1 7.6 5.1 8V8.7C5.1 9.2 4.7 9.6 4.2 9.6H3C2.4 9.6 2 9.2 2 8.6Z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
        </svg>
      )

    case 'wrong_number':
      return (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M7 1.8L12.6 11.6H1.4L7 1.8Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M7 5.6V8.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="7" cy="9.9" r="0.75" fill="currentColor" />
        </svg>
      )

    default:
      // Разбор ещё идёт или не удался.
      return (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="5.4" stroke="currentColor" strokeWidth="1.4" strokeDasharray="2.4 2" />
        </svg>
      )
  }
}

/**
 * Настроение собеседника — тремя ступенями.
 *
 * Не смайлик: рожица в рабочей таблице читается как игрушка и спорит
 * по весу с числами рядом. Три чёрточки разной высоты дают то же самое
 * — плохо, ровно, хорошо, — но остаются знаком, а не картинкой.
 */
export function SentimentIcon({ sentiment }: { sentiment?: string }) {
  const level = sentiment === 'positive' ? 3 : sentiment === 'negative' ? 1 : 2
  const color =
    sentiment === 'positive'
      ? 'var(--success)'
      : sentiment === 'negative'
        ? 'var(--danger)'
        : 'var(--text-muted)'

  return (
    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-label={label(sentiment)}>
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={i * 5}
          y={10 - (i + 1) * 3}
          width="3"
          height={(i + 1) * 3}
          rx="1"
          fill={i < level ? color : 'var(--surface-sunken)'}
        />
      ))}
    </svg>
  )
}

function label(sentiment?: string): string {
  if (sentiment === 'positive') return 'доброжелательно'
  if (sentiment === 'negative') return 'раздражённо'
  return 'спокойно'
}

/**
 * Кольцо оценки.
 *
 * Число внутри остаётся: кольцо даёт мгновенное «много или мало», число
 * — точность, когда нужно сравнить два соседних звонка.
 */
export function ScoreRing({ score, success }: { score: number; success: boolean }) {
  const radius = 12
  const circumference = 2 * Math.PI * radius
  const filled = (Math.min(Math.max(score, 0), 100) / 100) * circumference

  // Цвет по самой оценке, а не только по итогу: разговор с отказом и
  // оценкой семьдесят — это хорошо отработанный отказ, и красным он
  // выглядел бы провалом.
  const color =
    score >= 70
      ? 'var(--success)'
      : score >= 40
        ? 'var(--warning)'
        : score > 0
          ? 'var(--danger)'
          : 'var(--border-strong)'

  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-label={`Оценка ${score} из 100`}>
      <circle cx="15" cy="15" r={radius} fill="none" stroke="var(--surface-sunken)" strokeWidth="2" />
      <circle
        cx="15"
        cy="15"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        // Начинаем сверху, а не справа: заполнение по часовой от
        // двенадцати читается как шкала, от трёх — как случайная дуга.
        transform="rotate(-90 15 15)"
      />
      <text
        x="15"
        y="15.5"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="10.5"
        fontWeight="600"
        fill={success ? 'var(--text)' : 'var(--text-secondary)'}
      >
        {score}
      </text>
    </svg>
  )
}

/**
 * Скорость ответа агента.
 *
 * Число и точка состояния слева. Молния выглядела рекламной иконкой, а
 * здесь нужен индикатор: зелёный — живой темп, жёлтый — заминка
 * слышна, красный — собеседник успевает решить, что связь оборвалась.
 */
export function ReplySpeed({ ms }: { ms: number }) {
  if (!ms) return null

  const color =
    ms > 2500 ? 'var(--danger)' : ms > 1500 ? 'var(--warning)' : 'var(--success)'

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
      title={`агент отвечает за ${(ms / 1000).toFixed(1)} с`}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
        }}
      />
      {(ms / 1000).toFixed(1)}
    </span>
  )
}

/** Есть ли запись разговора. */
export function RecordingMark({ call }: { call: Call }) {
  if (!call.hasRecording) return null

  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-label="есть запись">
      <path d="M1 6.5V5.5M3.4 8.6V3.4M6 10V2M8.6 8.6V3.4M11 6.5V5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}
